/** Dispatched (e.g. from the chat's canvas-add summary) to fit the viewport to a set of nodes. */
export const CANVAS_FOCUS_NODES_EVENT = "canvas:focus-nodes";

export interface CanvasFocusNodesDetail {
  nodeIds: string[];
  /** Pixels at the right edge to keep clear, so the fit lands left of a docked chat panel. */
  insetRight?: number;
  /** Leave the selection alone; by default the fitted nodes become the selection. */
  select?: false;
}

/**
 * Fit the active canvas viewport to `nodeIds`. Returns true only if a mounted
 * canvas actually navigated: the event is cancelable and the canvas handler
 * calls preventDefault() once it has fit, so `false` covers both "no canvas is
 * mounted here" and "none of those nodes are on the canvas any more".
 */
export function emitCanvasFocusNodes(
  nodeIds: string[],
  options: { insetRight?: number; select?: false } = {},
): boolean {
  if (nodeIds.length === 0) return false;
  const event = new CustomEvent(CANVAS_FOCUS_NODES_EVENT, {
    detail: {
      nodeIds,
      ...(options.insetRight ? { insetRight: options.insetRight } : {}),
      ...(options.select === false ? { select: false } : {}),
    } satisfies CanvasFocusNodesDetail,
    cancelable: true,
  });
  // dispatchEvent returns false when a listener called preventDefault().
  return !window.dispatchEvent(event);
}

/**
 * Structural shape of the React Flow nodes {@link selectNavigableNodeIds}
 * inspects — kept local so this module stays dependency-free (and importable
 * from tests without mounting React Flow).
 */
export interface NavigableNodeLike {
  id: string;
  hidden?: boolean;
  measured?: { width?: number | null; height?: number | null };
}

/**
 * Narrow `nodeIds` to the ones `fitView` will actually move the viewport for.
 *
 * React Flow's own visibility predicate is
 * `n.measured.width && n.measured.height && (includeHiddenNodes || !n.hidden)`,
 * and we never pass `includeHiddenNodes`. So a node that is hidden (the rail's
 * "Hide on canvas" action) or not yet measured is silently skipped by fitView
 * even though it is still in the store — acking on mere store presence would
 * report success while the viewport never moved. Order is preserved and ids
 * are deduped.
 */
export function selectNavigableNodeIds(
  nodeIds: string[],
  nodes: NavigableNodeLike[],
): string[] {
  const byId = new Map(nodes.map((node) => [node.id, node]));
  const seen = new Set<string>();
  const navigable: string[] = [];
  for (const id of nodeIds) {
    if (seen.has(id)) continue;
    const node = byId.get(id);
    if (!node) continue;
    if (node.hidden === true) continue;
    if (!node.measured?.width || !node.measured?.height) continue;
    seen.add(id);
    navigable.push(id);
  }
  return navigable;
}
